import React, { useState, useCallback, useEffect } from 'react';
import { useShop } from '../../contexts/ShopContext';
import {
  Page,
  Layout,
  Card,
  Button,
  DataTable,
  Modal,
  TextField,
  FormLayout,
  Select,
  EmptyState,
  LegacyStack,
  Text,
  Badge,
  Banner,
  ButtonGroup,
  Popover,
  ActionList,
  ChoiceList,
  Spinner,
  Toast
} from '@shopify/polaris';
import { 
  PlusMinor, 
  DeleteMinor, 
  EditMinor, 
  DuplicateMinor,
  MobileVerticalDotsMajor,
  ViewMinor,
  DragHandleMinor
} from '@shopify/polaris-icons';
// Import dnd-kit libraries for drag-and-drop functionality
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  createSizechartGroup, 
  updateSizechartGroup, 
  deleteSizechartGroup,
  createSizechart,
  updateSizechart,
  deleteSizechart,
  generateConvertedSizechart
} from '../../services/sizechartService';
import { convertSizechartWithAI } from '../../services/sizechartAIService';

// Sortable Row component for drag-and-drop functionality
const SortableRow = ({ row, index, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.size
  });
  
  // Container style with relative positioning
  const containerStyle = {
    position: 'relative',
    marginBottom: '12px',
  };
  
  // Card style with drag-and-drop properties
  const cardStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? '#f4f6f8' : 'white',
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #dfe3e8',
    boxShadow: isDragging ? '0 5px 15px rgba(0,0,0,0.1)' : '0 2px 5px rgba(0,0,0,0.05)',
    cursor: 'move',
    zIndex: isDragging ? 10 : 1
  };
  
  // Handle delete button click
  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onRemove(index);
    return false;
  };
  
  return (
    <div style={containerStyle}>
      {/* Draggable card */}
      <div ref={setNodeRef} style={cardStyle} {...attributes} {...listeners}>
        <LegacyStack spacing="tight" alignment="center">
          <div style={{ color: '#637381', marginRight: '8px' }}>
            <DragHandleMinor />
          </div>
          <Text variant="bodyMd" fontWeight="bold">{row.size}</Text>
        </LegacyStack>
      </div>
      
      {/* Separate delete button positioned to look like it's part of the card */}
      <div 
        style={{
          position: 'absolute',
          top: '12px',  // Match card padding
          right: '16px', // Match card padding
          zIndex: 30
        }}
      >
        <Button
          plain
          destructive
          icon={DeleteMinor}
          onClick={handleDelete}
          accessibilityLabel="Remove size"
        />
      </div>
    </div>
  );
};

// Sortable Column component for drag-and-drop functionality
const SortableColumn = ({ column, index, onRemove }) => {
  // Use column name as the id for sorting
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.name
  });
  
  // Container style with relative positioning
  const containerStyle = {
    position: 'relative',
    marginBottom: '12px',
  };
  
  // Card style with drag-and-drop properties
  const cardStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? '#f4f6f8' : 'white',
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #dfe3e8',
    boxShadow: isDragging ? '0 5px 15px rgba(0,0,0,0.1)' : '0 2px 5px rgba(0,0,0,0.05)',
    cursor: 'move',
    zIndex: isDragging ? 10 : 1
  };
  
  // Handle delete button click
  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onRemove(index);
    return false;
  };
  
  return (
    <div style={containerStyle}>
      {/* Draggable card */}
      <div ref={setNodeRef} style={cardStyle} {...attributes} {...listeners}>
        <LegacyStack spacing="tight" alignment="center">
          <div style={{ color: '#637381', marginRight: '8px' }}>
            <DragHandleMinor />
          </div>
          <Text variant="bodyMd" fontWeight="bold">{column.name}</Text>
          {column.unit !== 'none' && (
            <Text variant="bodyMd" color="subdued">({column.unit})</Text>
          )}
        </LegacyStack>
      </div>
      
      {/* Separate delete button positioned to look like it's part of the card */}
      <div 
        style={{
          position: 'absolute',
          top: '12px',  // Match card padding
          right: '16px', // Match card padding
          zIndex: 30
        }}
      >
        <Button
          plain
          destructive
          icon={DeleteMinor}
          onClick={handleDelete}
          accessibilityLabel="Remove column"
        />
      </div>
    </div>
  );
};

export default function SizechartDesigner({ shopId, userId, groups, setGroups, sizecharts, setSizecharts, refreshData, settings }) {
  // Get current shop from context
  const { currentShop } = useShop();
  
  // State for clothing type (group) management
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [groupError, setGroupError] = useState('');
  
  // State for sizechart management
  const [isSizechartModalOpen, setIsSizechartModalOpen] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(''); // Empty string means 'All clothing types'
  const [sizechartName, setSizechartName] = useState('');
  const [sizechartDescription, setSizechartDescription] = useState('');
  const [sizechartCategory, setSizechartCategory] = useState('women'); // Default to women's clothing
  const [editingSizechartId, setEditingSizechartId] = useState(null);
  const [sizechartError, setSizechartError] = useState('');
  
  // State for column management
  const [columns, setColumns] = useState([{ name: 'Size', unit: 'none' }]);
  const [newColumnName, setNewColumnName] = useState('');
  const [newColumnUnit, setNewColumnUnit] = useState('cm');
  const [newColumnCountry, setNewColumnCountry] = useState('');
  
  // State for row management
  const [rows, setRows] = useState([{ size: 'S', values: [] }]);
  const [newRowSize, setNewRowSize] = useState('');
  
  // State for country selection
  const [selectedCountries, setSelectedCountries] = useState(['US']);
  
  // State for size conversion
  const [isConversionModalOpen, setIsConversionModalOpen] = useState(false);
  const [conversionSourceSizechart, setConversionSourceSizechart] = useState(null);
  const [conversionTargetCountry, setConversionTargetCountry] = useState('');
  const [conversionCategory, setConversionCategory] = useState('women');
  const [isConverting, setIsConverting] = useState(false);
  const [conversionError, setConversionError] = useState('');
  
  // State for auto-generating country sizes
  const [isGeneratingSizes, setIsGeneratingSizes] = useState(false);
  const [sizeConversionModel, setSizeConversionModel] = useState(null);
  const [creditCost, setCreditCost] = useState(1); // Default credit cost
  
  // State for chart preview
  const [isChartPreviewModalOpen, setIsChartPreviewModalOpen] = useState(false);
  const [previewSizechart, setPreviewSizechart] = useState(null);
  
  // State for toast notifications
  const [toastActive, setToastActive] = useState(false);
  const [toastContent, setToastContent] = useState('');
  const [toastError, setToastError] = useState(false);
  
  // Toast handlers
  const toggleToast = useCallback(() => setToastActive((active) => !active), []);
  
  // Chart preview modal handler
  const openChartPreviewModal = (chart) => {
    setPreviewSizechart(chart);
    setIsChartPreviewModalOpen(true);
  };
  
  // Auto-generate country sizes handler
  const handleGenerateCountrySizes = async () => {
    if (rows.length === 0) {
      setToastContent('Please add at least one size before generating country sizes');
      setToastError(true);
      setToastActive(true);
      return;
    }
    
    // Check if settings and countries are available
    if (!settings || !settings.countries || settings.countries.length === 0) {
      console.log('Settings:', settings);
      console.log('Countries in settings:', settings?.countries);
      setToastContent('Please configure at least one country in the Settings tab');
      setToastError(true);
      setToastActive(true);
      return;
    }
    
    setIsGeneratingSizes(true);
    
    try {
      // Create a temporary sizechart object with current data
      const tempSizechart = {
        name: sizechartName || 'Temporary Sizechart',
        description: sizechartDescription || '',
        category: sizechartCategory,
        countries: [settings?.countries[0] || 'US'], // Use first country as source
        columns,
        rows
      };
      
      // Get the source country from the current sizechart
      const sourceCountry = tempSizechart.countries[0] || 'US';
      console.log('Source country:', sourceCountry);
      
      // Get target countries - if only one country is available, use it for both source and target
      let targetCountries = [];
      
      if (settings.countries.length === 1) {
        // If only one country is available, use it for both source and target
        targetCountries = [...settings.countries];
        console.log('Only one country available, using it for both source and target:', targetCountries);
      } else {
        // Get all countries from settings except the source country
        targetCountries = settings.countries.filter(country => 
          country !== sourceCountry && country.trim() !== ''
        );
        console.log('Target countries:', targetCountries);
        
        // If no target countries after filtering, use the source country
        if (targetCountries.length === 0) {
      }
    }
    
    // Get the existing country columns
    const existingCountryColumns = columns.filter(col => col.country).map(col => col.country);
    
    // Initialize new columns and rows arrays
    let newColumns = [...columns];
    let newRows = [...rows];
    
    // Process each target country one by one
    for (const country of targetCountries) {
      // Skip if this country already has columns
      if (existingCountryColumns.includes(country)) {
        console.log(`Country ${country} already has columns, skipping...`);
        continue;
      }
      
      console.log(`Processing country: ${country}`);
      
      try {
        // First, get the AI response for this country
        const result = await convertSizechartWithAI(
          userId, // Use the actual user ID for credit tracking
          shopId,
          tempSizechart,
          country,
          sizechartCategory
        );
        
        console.log(`AI result for ${country}:`, result);
        
        // Check if we have AI-converted sizes
        if (!result.convertedSizes || result.convertedSizes.length === 0) {
          console.error(`No converted sizes returned for ${country}`);
          continue;
        }
        
        // Determine the appropriate unit for this country based on the AI response
        const aiUnit = result.convertedSizes[0]?.unit || 'in';
        console.log(`Using unit ${aiUnit} for ${country} based on AI response`);
        
        // First add a size column for this country
        const sizeCol = {
          name: `Size (${country})`,
          unit: 'none',
          country: country
        };
        
        newColumns.push(sizeCol);
        
        // Add empty values for this column to each row
        newRows = newRows.map(row => ({
          ...row,
          values: [...row.values, '']
        }));
        
        // Get all base measurement columns (without country)
        const measurementColumns = columns.filter(col => col.unit !== 'none' && !col.country);
        
        // For each measurement column, add a country-specific version with the correct unit
        for (const measureCol of measurementColumns) {
          const countryCol = {
            name: `${measureCol.name} (${country})`,
            unit: aiUnit, // Use the unit from the AI response
            country: country
          };
          
          newColumns.push(countryCol);
          
          // Add empty values for this column to each row
          newRows = newRows.map(row => ({
            ...row,
            values: [...row.values, '']
          }));
        }
        
        // Now update the rows with the values from the AI response
        newRows = newRows.map((row, rowIndex) => {
          // Get the converted size from the AI response
          const aiSizeData = result.convertedSizes[rowIndex];
          if (!aiSizeData) return row;
          
          // Create a copy of the row values
          const newValues = [...row.values];
          
          // Update the size column for this country if it exists
          const sizeColumnIndex = newColumns.findIndex(col => 
            col.name.toLowerCase() === `size (${country.toLowerCase()})` || 
            col.name.toLowerCase() === `${country.toLowerCase()} size`
          );
          
          if (sizeColumnIndex > 0) { // 0 is the Size column which we don't update
            newValues[sizeColumnIndex - 1] = aiSizeData.convertedSize;
            console.log(`Updated size column ${sizeColumnIndex} for ${country} with value: ${aiSizeData.convertedSize}`);
          }
          
          // Find all measurement columns for this country
          const countryMeasurementColumns = newColumns.filter(col => 
            col.country === country && col.unit !== 'none'
          );
          
          // Check if we have measurements from the AI response
          if (aiSizeData.measurements) {
            // Use the AI-provided measurements
            const aiMeasurements = aiSizeData.measurements;
            
            // Map each country measurement column to the corresponding AI measurement
            countryMeasurementColumns.forEach(countryCol => {
              // Extract the base name from the country column name
              // e.g., 'Bust (UK)' -> 'Bust'
              const baseName = countryCol.name.split(' (')[0].toLowerCase();
              
              // Find the matching measurement in the AI response
              const measurementValue = aiMeasurements[baseName];
              
              if (measurementValue) {
                // Find the index of the country column
                const countryColIndex = newColumns.findIndex(col => col.name === countryCol.name) - 1;
                
                if (countryColIndex >= 0) {
                  // Set the value directly from the AI response
                  newValues[countryColIndex] = measurementValue;
                  console.log(`Updated measurement column ${countryColIndex} for ${country} with AI value: ${measurementValue} (unit: ${aiUnit})`);
                }
              }
            });
          } else {
            console.log(`No measurements found in AI response for row ${rowIndex}`);
          }
          
          return {
            ...row,
            values: newValues
          };
        });
      } catch (error) {
        console.error(`Error processing country ${country}:`, error);
        setToastContent(`Error processing country ${country}: ${error.message}`);
        setToastError(true);
        setToastActive(true);
      }
    }
    
    // Update the state with the new columns and rows
    setColumns(newColumns);
    setRows(newRows);
    
    setToastContent('Successfully generated country-specific sizes');
    setToastError(false);
    setToastActive(true);
  } catch (error) {
    console.error('Error generating country sizes:', error);
    setToastContent(`Error generating country sizes: ${error.message}`);
    setToastError(true);
    setToastActive(true);
  } finally {
    setIsGeneratingSizes(false);
  }
};
  
  const toastMarkup = toastActive ? (
    <Toast content={toastContent} error={toastError} onDismiss={toggleToast} />
  ) : null;
  
  // State for action menu
  const [activeGroupPopover, setActiveGroupPopover] = useState(null);
  const [activeSizechartPopover, setActiveSizechartPopover] = useState(null);
  
  // Effect to reset to 'All clothing types' when groups change
  useEffect(() => {
    // Reset to 'All clothing types' (empty string)
    setSelectedGroupId('');
  }, [groups.length]);
  
  // Group modal handlers
  const openGroupModal = useCallback(() => {
    setIsGroupModalOpen(true);
    setGroupName('');
    setGroupDescription('');
    setEditingGroupId(null);
    setGroupError('');
  }, []);
  
  const openEditGroupModal = useCallback((group) => {
    setIsGroupModalOpen(true);
    setGroupName(group.name);
    setGroupDescription(group.description || '');
    setEditingGroupId(group.id);
    setGroupError('');
    setActiveGroupPopover(null);
  }, []);
  
  const closeGroupModal = useCallback(() => {
    setIsGroupModalOpen(false);
  }, []);
  
  // Sizechart modal handlers
  const openSizechartModal = useCallback(() => {
    setIsSizechartModalOpen(true);
    setSizechartName('');
    setSizechartDescription('');
    // Don't set selectedGroupId here to maintain the current filter
    setEditingSizechartId(null);
    setSizechartError('');
    setColumns([]);
    setRows([]);
    // Use the shop's countries from settings
    setSelectedCountries(settings?.countries || ['US']);
  }, [settings]);
  
  useEffect(() => {
    const fetchModelInfo = async () => {
      try {
        if (settings?.sizeConversionModelId) {
          // Import the aiModelService to get the model details
          const { getAIModelById } = await import('../../services/aiModelService');
          const model = await getAIModelById(settings.sizeConversionModelId);
          
          if (model) {
            setSizeConversionModel(model);
            setCreditCost(model.creditCostPerOutput || 1);
            console.log('Size conversion model:', model);
          }
        }
      } catch (error) {
        console.error('Error fetching AI model information:', error);
      }
    };
    
    fetchModelInfo();
  }, [settings?.sizeConversionModelId]);
  
  const openEditSizechartModal = useCallback((sizechart) => {
    setIsSizechartModalOpen(true);
    setSizechartName(sizechart.name);
    setSizechartDescription(sizechart.description || '');
    setSelectedGroupId(sizechart.groupId);
    setSizechartCategory(sizechart.category || 'women');
    setEditingSizechartId(sizechart.id);
    setSizechartError('');
    setColumns(sizechart.columns || [{ name: 'Size', unit: 'none' }]);
    setRows(sizechart.rows || [{ size: 'S', values: [] }]);
    // Always use the shop's countries from settings
    setSelectedCountries(settings?.countries || ['US']);
    setActiveSizechartPopover(null);
  }, [settings]);
  
  const closeSizechartModal = useCallback(() => {
    setIsSizechartModalOpen(false);
    setSizechartName('');
    setSizechartDescription('');
    // Don't reset the selectedGroupId to maintain the current filter
    // Keep the current filter selection instead of resetting to the first group
    setSizechartCategory('women');
    setEditingSizechartId(null);
    setSizechartError('');
    setColumns([]);
    setRows([]);
  }, []);
  
  // Group CRUD operations
  const handleSaveGroup = async () => {
    try {
      if (!groupName.trim()) {
        setGroupError('Group name is required');
        return;
      }
      
      if (editingGroupId) {
        // Update existing group
        const updatedGroup = await updateSizechartGroup(shopId, editingGroupId, {
          name: groupName,
          description: groupDescription
        });
        
        setGroups(prevGroups => prevGroups.map(group => 
          group.id === editingGroupId ? updatedGroup : group
        ));
      } else {
        // Create new group
        const newGroup = await createSizechartGroup(shopId, {
          name: groupName,
          description: groupDescription
        });
        
        setGroups(prevGroups => [...prevGroups, newGroup]);
        setSelectedGroupId(newGroup.id);
      }
      
      closeGroupModal();
    } catch (error) {
      console.error('Error saving group:', error);
      setGroupError(error.message || 'Failed to save group');
    }
  };
  
  const handleDeleteGroup = async (groupId) => {
    if (window.confirm('Are you sure you want to delete this clothing type? This will also delete all associated sizecharts.')) {
      try {
        await deleteSizechartGroup(shopId, groupId);
        
        // Update state
        setGroups(prevGroups => prevGroups.filter(group => group.id !== groupId));
        setSizecharts(prevSizecharts => prevSizecharts.filter(chart => chart.groupId !== groupId));
        
        // If the deleted group was selected, select the first available group
        if (selectedGroupId === groupId && groups.length > 1) {
          const remainingGroups = groups.filter(group => group.id !== groupId);
          if (remainingGroups.length > 0) {
            setSelectedGroupId(remainingGroups[0].id);
          } else {
            setSelectedGroupId('');
          }
        }
        
        setActiveGroupPopover(null);
      } catch (error) {
        console.error('Error deleting group:', error);
        alert('Failed to delete clothing type: ' + (error.message || 'Unknown error'));
      }
    }
  };
  
  // Sizechart CRUD operations
  const handleSaveSizechart = async () => {
    try {
      // Validate inputs
      if (!sizechartName.trim()) {
        setSizechartError('Name is required');
        return;
      }
      
      if (!selectedGroupId) {
        setSizechartError('Clothing type is required');
        return;
      }
      
      // Validate columns and rows
      if (columns.length < 2) {
        setSizechartError('At least one measurement column is required');
        return;
      }
      
      if (rows.length === 0) {
        setSizechartError('At least one size row is required');
        return;
      }
      
      // Ensure all rows have values for all columns
      const validatedRows = rows.map(row => {
        const rowValues = row.values || [];
        while (rowValues.length < columns.length - 1) {
          rowValues.push('');
        }
        return {
          ...row,
          values: rowValues
        };
      });
      
      // Create or update sizechart
      const sizechartData = {
        name: sizechartName.trim(),
        description: sizechartDescription.trim(),
        groupId: selectedGroupId,
        category: sizechartCategory,
        countries: selectedCountries,
        columns,
        rows: validatedRows
      };
      
      if (editingSizechartId) {
        // Update existing sizechart
        const updatedSizechart = await updateSizechart(shopId, editingSizechartId, sizechartData);
        
        setSizecharts(prevSizecharts => prevSizecharts.map(chart => 
          chart.id === editingSizechartId ? updatedSizechart : chart
        ));
      } else {
        // Create new sizechart
        const newSizechart = await createSizechart(shopId, {
          name: sizechartName,
          description: sizechartDescription,
          groupId: selectedGroupId,
          columns,
          rows: validatedRows,
          countries: selectedCountries
        });
        
        setSizecharts(prevSizecharts => [...prevSizecharts, newSizechart]);
      }
      
      closeSizechartModal();
    } catch (error) {
      console.error('Error saving sizechart:', error);
      setSizechartError(error.message || 'Failed to save sizechart');
    }
  };
  
  const handleDeleteSizechart = async (sizechartId) => {
    if (window.confirm('Are you sure you want to delete this sizechart?')) {
      try {
        await deleteSizechart(shopId, sizechartId);
        
        // Update state
        setSizecharts(prevSizecharts => prevSizecharts.filter(chart => chart.id !== sizechartId));
        setActiveSizechartPopover(null);
      } catch (error) {
        console.error('Error deleting sizechart:', error);
        alert('Failed to delete sizechart: ' + (error.message || 'Unknown error'));
      }
    }
  };
  
  const handleDuplicateSizechart = async (sizechart) => {
    try {
      // Create a duplicate with a new name
      const duplicate = {
        ...sizechart,
        id: undefined, // Remove ID so a new one will be generated
        name: `${sizechart.name} (Copy)`
      };
      
      const newSizechart = await createSizechart(shopId, duplicate);
      setSizecharts([...sizecharts, newSizechart]);
      setActiveSizechartPopover(null);
    } catch (error) {
      console.error('Error duplicating sizechart:', error);
      alert('Failed to duplicate sizechart: ' + (error.message || 'Unknown error'));
    }
  };
  
  // Open the size conversion modal
  const openConversionModal = (sizechart) => {
    setConversionSourceSizechart(sizechart);
    setConversionTargetCountry('');
    setIsConversionModalOpen(true);
    setActiveSizechartPopover(null);
  };
  
  // Close the size conversion modal
  const closeConversionModal = () => {
    setIsConversionModalOpen(false);
    setConversionSourceSizechart(null);
    setConversionTargetCountry('');
    setConversionError('');
  };
  
  // Handle size conversion
  const handleConvertSizechart = async () => {
    if (!conversionTargetCountry) {
      setConversionError('Please select a target country');
      return;
    }
    
    setIsConverting(true);
    setConversionError('');
    
    try {
      // Use AI to convert the sizechart
      const result = await convertSizechartWithAI(
        shopId,
        conversionSourceSizechart,
        conversionTargetCountry,
        conversionCategory
      );
      
      // Save the converted sizechart
      await createSizechart(shopId, result.convertedSizechart);
      
      // Show success toast with any notes from the AI
      setToastContent(`Sizechart converted successfully to ${conversionTargetCountry} sizing`);
      setToastError(false);
      setToastActive(true);
      
      closeConversionModal();
      refreshData();
    } catch (error) {
      console.error('Error converting sizechart:', error);
      setConversionError(error.message || 'Error converting sizechart');
    } finally {
      setIsConverting(false);
    }
  };
  
  // Column management
  const handleAddColumn = () => {
    if (!newColumnName.trim()) {
      alert('Column name is required');
      return;
    }
    
    // Check for duplicate column name
    if (columns.some(col => col.name.toLowerCase() === newColumnName.toLowerCase())) {
      alert('A column with this name already exists');
      return;
    }
    
    // Prepare column name with country code if specified
    let columnName = newColumnName;
    if (newColumnCountry) {
      // Avoid redundancy like "UK (UK)" - only add country code if it's not already in the name
      if (!newColumnName.includes(newColumnCountry)) {
        columnName = `${newColumnName} (${newColumnCountry})`;
      }
    }
    
    setColumns([...columns, { 
      name: columnName, 
      unit: newColumnUnit,
      country: newColumnCountry || null
    }]);
    
    setNewColumnName('');
    setNewColumnUnit('cm');
    setNewColumnCountry('');
    
    // Add empty value for this column to each row
    setRows(rows.map(row => ({
      ...row,
      values: [...(row.values || []), '']
    })));
  };
  
  const handleRemoveColumn = (index) => {
    // Don't allow removing the Size column
    if (index === 0) return;
    
    const newColumns = [...columns];
    newColumns.splice(index, 1);
    setColumns(newColumns);
    
    // Remove the corresponding value from each row
    setRows(rows.map(row => {
      const newValues = [...(row.values || [])];
      newValues.splice(index - 1, 1); // Adjust index because Size is not in values array
      return {
        ...row,
        values: newValues
      };
    }));
  };
  
  // Row management
  const handleAddRow = () => {
    if (!newRowSize.trim()) {
      alert('Size name is required');
      return;
    }
    
    // Check for duplicate size
    if (rows.some(row => row.size.toLowerCase() === newRowSize.toLowerCase())) {
      alert('A row with this size already exists');
      return;
    }
    
    // Create empty values for all columns
    // If there are no columns yet, use an empty array
    const emptyValues = columns.length > 0 ? Array(columns.length).fill('') : [];
    
    setRows([...rows, { size: newRowSize, values: emptyValues }]);
    setNewRowSize('');
  };
  
  const handleRemoveRow = (index) => {
    const newRows = [...rows];
    newRows.splice(index, 1);
    setRows(newRows);
  };
  
  // Set up sensors for drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Handle drag-and-drop reordering of rows
  const handleRowDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      // Find the indices of the rows being reordered
      const oldIndex = rows.findIndex(row => row.size === active.id);
      const newIndex = rows.findIndex(row => row.size === over.id);
      
      // Create a new rows array with the updated order
      // This moves the entire row with all its values intact
      const newRows = arrayMove([...rows], oldIndex, newIndex);
      
      // Update state with the new rows
      setRows(newRows);
    }
  };
  
  // Handle drag-and-drop reordering of columns
  const handleColumnDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      // Find the indices of the columns being reordered
      const oldIndex = columns.findIndex(col => col.name === active.id);
      const newIndex = columns.findIndex(col => col.name === over.id);
      
      // Create a new columns array with the updated order
      const newColumns = arrayMove([...columns], oldIndex, newIndex);
      
      // Create a mapping of how column indices have changed
      // We need this to correctly reorder values in each row
      const valueIndexMap = [];
      
      // Skip the Size column (index 0) as it's not in the values array
      for (let i = 1; i < columns.length; i++) {
        // Find where this column ended up in the new order
        const newPos = newColumns.findIndex(col => col.name === columns[i].name);
        valueIndexMap.push({ 
          oldValueIndex: i - 1, // Adjust for Size column not being in values array
          newValueIndex: newPos - 1 // Adjust for Size column not being in values array
        });
      }
      
      // Update row values to match the new column order
      const newRows = rows.map(row => {
        // If row has no values, just return it unchanged
        if (!row.values || row.values.length === 0) return row;
        
        // Create a new values array with the same length as the original
        const newValues = Array(row.values.length).fill('');
        
        // Map each value to its new position
        valueIndexMap.forEach(mapping => {
          // Only copy values that exist in the original array
          if (mapping.oldValueIndex < row.values.length) {
            newValues[mapping.newValueIndex] = row.values[mapping.oldValueIndex];
          }
        });
        
        return {
          ...row,
          values: newValues
        };
      });
      
      // Update state with the new columns and rows
      setColumns(newColumns);
      setRows(newRows);
    }
  };
  
  const handleCellChange = (rowIndex, colIndex, value) => {
    const newRows = [...rows];
    if (!newRows[rowIndex].values) {
      newRows[rowIndex].values = [];
    }
    newRows[rowIndex].values[colIndex] = value;
    setRows(newRows);
  };
  
  // Handle country selection
  const handleCountryChange = (selected) => {
    setSelectedCountries(selected);
  };
  
  // Prepare data for group table
  const groupRows = groups.map((group, index) => [
    <Text variant="bodyMd" fontWeight="bold">{group.name}</Text>,
    <Text variant="bodyMd">{group.description || '-'}</Text>,
    sizecharts.filter(chart => chart.groupId === group.id).length,
    <ButtonGroup>
      <Button size="slim" onClick={() => openEditGroupModal(group)}>Edit</Button>
      <Popover
        active={activeGroupPopover === group.id}
        activator={<Button size="slim" icon={MobileVerticalDotsMajor} onClick={() => setActiveGroupPopover(activeGroupPopover === group.id ? null : group.id)} />}
        onClose={() => setActiveGroupPopover(null)}
      >
        <ActionList
          items={[
            {
              content: 'Delete',
              icon: DeleteMinor,
              destructive: true,
              onAction: () => handleDeleteGroup(group.id)
            }
          ]}
        />
      </Popover>
    </ButtonGroup>
  ]);
  
  // Prepare data for sizechart table
  const sizechartRows = sizecharts
    .filter(chart => !selectedGroupId || chart.groupId === selectedGroupId)
    .map((chart, index) => [
      <Text variant="bodyMd" fontWeight="bold">{chart.name}</Text>,
      <Text variant="bodyMd">{chart.description || '-'}</Text>,
      <Text variant="bodyMd">{groups.find(g => g.id === chart.groupId)?.name || '-'}</Text>,
      <Badge>{chart.category || 'women'}</Badge>,
      <LegacyStack spacing="tight">
        {(chart.countries || ['US']).map(country => (
          <Badge key={country}>{country}</Badge>
        ))}
      </LegacyStack>,
      <ButtonGroup>
        <Button size="slim" onClick={() => openEditSizechartModal(chart)}>Edit</Button>
        <Popover
          active={activeSizechartPopover === chart.id}
          activator={<Button size="slim" icon={MobileVerticalDotsMajor} onClick={() => setActiveSizechartPopover(activeSizechartPopover === chart.id ? null : chart.id)} />}
          onClose={() => setActiveSizechartPopover(null)}
        >
          <ActionList
            items={[
              {
                content: 'View Chart',
                icon: ViewMinor,
                onAction: () => openChartPreviewModal(chart)
              },
              {
                content: 'Duplicate',
                icon: DuplicateMinor,
                onAction: () => handleDuplicateSizechart(chart)
              },
              {
                content: 'Delete',
                icon: DeleteMinor,
                destructive: true,
                onAction: () => handleDeleteSizechart(chart.id)
              }
            ]}
          />
        </Popover>
      </ButtonGroup>
    ]);
  
  // Prepare data for sizechart preview table
  const previewHeaders = columns.map(col => {
    return col.name;
  });
  
  const previewRows = rows.map(row => {
    // Start with the size as the first column
    const rowData = [row.size];
    
    // For each column (except the first 'Size' column), add the corresponding value
    if (row.values && row.values.length > 0) {
      // Add values for each column after the Size column
      for (let i = 1; i < columns.length; i++) {
        // The column index in the values array is i-1 (since Size column is not in values)
        const valueIndex = i - 1;
        // Add the value if it exists, otherwise add an empty string
        rowData.push(valueIndex < row.values.length ? row.values[valueIndex] : '');
      }
    } else {
      // If no values exist, fill with empty strings
      for (let i = 1; i < columns.length; i++) {
        rowData.push('');
      }
    }
    
    return rowData;
  });
  
  // Country options for select
  const countryOptions = settings?.customCountries ? 
    [
      { label: 'United States (US)', value: 'US' },
      { label: 'United Kingdom (UK)', value: 'UK' },
      { label: 'European Union (EU)', value: 'EU' },
      { label: 'Australia (AU)', value: 'AU' },
      { label: 'New Zealand (NZ)', value: 'NZ' },
      { label: 'Canada (CA)', value: 'CA' },
      { label: 'Japan (JP)', value: 'JP' },
      { label: 'China (CN)', value: 'CN' },
      { label: 'South Korea (KR)', value: 'KR' },
      { label: 'Russia (RU)', value: 'RU' },
      { label: 'Brazil (BR)', value: 'BR' },
      { label: 'Mexico (MX)', value: 'MX' },
      { label: 'India (IN)', value: 'IN' },
      ...(settings?.customCountries.map(c => ({
        label: `${c.name} (${c.code})`,
        value: c.code
      })))
    ].filter(country => settings?.countries?.includes(country.value)) : 
    [
      { label: 'United States (US)', value: 'US' },
      { label: 'United Kingdom (UK)', value: 'UK' },
      { label: 'European Union (EU)', value: 'EU' },
      { label: 'Australia (AU)', value: 'AU' },
      { label: 'New Zealand (NZ)', value: 'NZ' }
    ];
  
  // Group options for select
  const groupOptions = groups.map(group => ({
    label: group.name,
    value: group.id
  }));
  
  // Unit options for select
  const unitOptions = [
    { label: 'Centimeters (cm)', value: 'cm' },
    { label: 'Inches (in)', value: 'inch' },
    { label: 'None', value: 'none' }
  ];
  
  return (
    <Layout>
      {/* Clothing Types (Groups) Section */}
      <Layout.Section>
        <Card title="Clothing Types">
          <Card.Section>
            <LegacyStack distribution="trailing">
              <Button primary onClick={openGroupModal}>Add Clothing Type</Button>
            </LegacyStack>
          </Card.Section>
          
          <Card.Section>
            {groups.length === 0 ? (
              <EmptyState
                heading="No clothing types yet"
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>Add a clothing type to start creating sizecharts.</p>
              </EmptyState>
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'numeric', 'text']}
                headings={['Name', 'Description', 'Sizecharts', 'Actions']}
                rows={groupRows}
              />
            )}
          </Card.Section>
        </Card>
      </Layout.Section>
      
      {/* Sizecharts Section */}
      <Layout.Section>
        <Card title="Sizecharts">
          <Card.Section>
            <LegacyStack distribution="equalSpacing">
              <Select
                label="Filter by clothing type"
                options={[{ label: 'All clothing types', value: '' }, ...groupOptions]}
                value={selectedGroupId}
                onChange={setSelectedGroupId}
                labelHidden
                helpText="Filter sizecharts by clothing type"
              />
              <Button 
                primary 
                onClick={openSizechartModal}
                disabled={groups.length === 0}
              >
                Add Sizechart
              </Button>
            </LegacyStack>
          </Card.Section>
          
          <Card.Section>
            {groups.length === 0 ? (
              <Banner status="info">
                <p>Please add a clothing type first before creating sizecharts.</p>
              </Banner>
            ) : sizecharts.length === 0 ? (
              <EmptyState
                heading="No sizecharts yet"
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>Add a sizechart template to start managing product sizes.</p>
              </EmptyState>
            ) : sizechartRows.length === 0 ? (
              <Banner status="info">
                <p>No sizecharts found for the selected clothing type.</p>
              </Banner>
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                headings={['Name', 'Description', 'Clothing Type', 'Category', 'Countries', 'Actions']}
                rows={sizechartRows}
              />
            )}
          </Card.Section>
        </Card>
      </Layout.Section>
      
      {/* Group Modal */}
      <Modal
        open={isGroupModalOpen}
        onClose={closeGroupModal}
        title={editingGroupId ? 'Edit Clothing Type' : 'Add Clothing Type'}
        primaryAction={{
          content: 'Save',
          onAction: handleSaveGroup
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: closeGroupModal
          }
        ]}
      >
        <Modal.Section>
          <FormLayout>
            {groupError && (
              <Banner status="critical">
                <p>{groupError}</p>
              </Banner>
            )}
            
            <TextField
              label="Name"
              value={groupName}
              onChange={setGroupName}
              autoComplete="off"
              error={!groupName.trim() ? 'Name is required' : undefined}
            />
            
            <TextField
              label="Description (optional)"
              value={groupDescription}
              onChange={setGroupDescription}
              autoComplete="off"
              multiline={3}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
      
      {/* Sizechart Modal */}
      <Modal
        open={isSizechartModalOpen}
        onClose={closeSizechartModal}
        title={editingSizechartId ? 'Edit Sizechart' : 'Add Sizechart'}
        primaryAction={{
          content: 'Save',
          onAction: handleSaveSizechart
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: closeSizechartModal
          }
        ]}
        large
      >
        <Modal.Section>
          <FormLayout>
            {sizechartError && (
              <Banner status="critical">
                <p>{sizechartError}</p>
              </Banner>
            )}
            
            <FormLayout.Group>
              <TextField
                label="Name"
                value={sizechartName}
                onChange={setSizechartName}
                autoComplete="off"
                error={!sizechartName.trim() ? 'Name is required' : undefined}
              />
              
              <Select
                label="Clothing Type"
                options={groupOptions}
                value={selectedGroupId}
                onChange={setSelectedGroupId}
                error={!selectedGroupId ? 'Clothing type is required' : undefined}
              />
            </FormLayout.Group>
            
            <FormLayout.Group>
              <Select
                label="Clothing Category"
                options={[
                  { label: "Women's Clothing", value: 'women' },
                  { label: "Men's Clothing", value: 'men' },
                  { label: "Kids's Clothing", value: 'kids' }
                ]}
                value={sizechartCategory}
                onChange={setSizechartCategory}
                helpText="Select the clothing category for accurate size conversion"
              />
            </FormLayout.Group>
            
            <TextField
              label="Description (optional)"
              value={sizechartDescription}
              onChange={setSizechartDescription}
              autoComplete="off"
              multiline={2}
            />
            
            {/* Country selection removed - using shop settings instead */}
            <Banner status="info">
              <p>This sizechart will use the countries configured in your shop settings.</p>
              <p>Currently supporting: {selectedCountries.join(', ')}</p>
            </Banner>
            
            {/* Column Management */}
            <Card title="Measurements">
              <Card.Section>
                <FormLayout>
                  <FormLayout.Group>
                    <TextField
                      label="Column Name"
                      value={newColumnName}
                      onChange={setNewColumnName}
                      autoComplete="off"
                      helpText="e.g., Bust, Waist, Hip, etc."
                    />
                    
                    <Select
                      label="Unit"
                      options={unitOptions}
                      value={newColumnUnit}
                      onChange={setNewColumnUnit}
                    />
                  </FormLayout.Group>
                  
                  <FormLayout.Group>
                    <Select
                      label="Country (Optional)"
                      options={[
                        { label: 'None', value: '' },
                        ...countryOptions
                      ]}
                      value={newColumnCountry}
                      onChange={setNewColumnCountry}
                      helpText="Specify if this column is for a specific country's sizing"
                    />
                    
                    <div style={{ marginTop: '2rem' }}>
                      <Button primary onClick={handleAddColumn}>Add Column</Button>
                    </div>
                  </FormLayout.Group>
                </FormLayout>
              </Card.Section>
              
              <Card.Section title="Current Measurements">
                {columns.length > 0 ? (
                  <DndContext 
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleColumnDragEnd}
                  >
                    <SortableContext 
                      items={columns.map(col => col.name)} 
                      strategy={verticalListSortingStrategy}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {columns.map((column, index) => (
                          <SortableColumn 
                            key={column.name} 
                            column={column} 
                            index={index} 
                            onRemove={handleRemoveColumn} 
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <Banner status="info">
                    <p>Add measurement columns to build your sizechart.</p>
                  </Banner>
                )}
              </Card.Section>
            </Card>
            
            {/* Row Management */}
            <Card title="Sizes">
              <Card.Section>
                <FormLayout>
                  <FormLayout.Group>
                    <TextField
                      label="Size Name"
                      value={newRowSize}
                      onChange={setNewRowSize}
                      autoComplete="off"
                      helpText="e.g., XS, S, M, L, XL, etc."
                    />
                    
                    <div style={{ marginTop: '2rem' }}>
                      <Button onClick={handleAddRow} icon={PlusMinor}>Add Size</Button>
                    </div>
                  </FormLayout.Group>
                  
                  <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                    <Banner status="info">
                      <LegacyStack distribution="equalSpacing" alignment="center">
                        <div>
                          <p><strong>Need country-specific sizes?</strong></p>
                          <p>Our AI can generate equivalent sizes for all your supported countries.</p>
                        </div>
                        <LegacyStack spacing='tight' alignment='center'>
                          <Button 
                            primary 
                            onClick={handleGenerateCountrySizes}
                            loading={isGeneratingSizes}
                            disabled={isGeneratingSizes || rows.length === 0 || !settings?.countries?.length}
                          >
                            Auto-Generate Country Sizes
                          </Button>
                          {settings?.sizeConversionModelId && (
                            <Badge status='info'>
                              {creditCost} {creditCost === 1 ? 'credit' : 'credits'}
                            </Badge>
                          )}
                        </LegacyStack>
                      </LegacyStack>
                    </Banner>
                  </div>
                </FormLayout>
              </Card.Section>
              
              <Card.Section title="Current Sizes">
                {rows.length > 0 ? (
                  <DndContext 
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleRowDragEnd}
                  >
                    <SortableContext 
                      items={rows.map(row => row.size)} 
                      strategy={verticalListSortingStrategy}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {rows.map((row, index) => (
                          <SortableRow 
                            key={row.size} 
                            row={row} 
                            index={index} 
                            onRemove={handleRemoveRow} 
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <Banner status="info">
                    <p>Add sizes to build your sizechart.</p>
                  </Banner>
                )}
              </Card.Section>
            </Card>
            
            {/* Sizechart Preview */}
            <Card title="Sizechart Preview">
              <Card.Section>
                {columns.length > 1 && rows.length > 0 ? (
                  <DataTable
                    columnContentTypes={Array(columns.length).fill('text')}
                    headings={previewHeaders}
                    rows={previewRows}
                    truncate={false}
                  />
                ) : (
                  <Banner status="info">
                    <p>Add measurements and sizes to preview your sizechart.</p>
                  </Banner>
                )}
              </Card.Section>
            </Card>
            
            {/* Measurement Values */}
            {columns.length > 1 && rows.length > 0 && (
              <Card title="Measurement Values">
                <Card.Section>
                  <FormLayout>
                    {rows.map((row, rowIndex) => (
                      <FormLayout.Group key={`values-${rowIndex}`} condensed>
                        <Text variant="bodyMd" fontWeight="bold">{row.size}</Text>
                        
                        {columns.slice(1).map((col, colIndex) => (
                          <TextField
                            key={`value-${rowIndex}-${colIndex}`}
                            label={`${row.size} - ${col.name}`}
                            labelHidden
                            value={(row.values && row.values[colIndex]) || ''}
                            onChange={(value) => handleCellChange(rowIndex, colIndex, value)}
                            autoComplete="off"
                            type="text"
                            suffix={col.unit !== 'none' ? col.unit : undefined}
                            helpText={colIndex === 0 ? 'Enter a single value or range (e.g., 34 or 34-38)' : undefined}
                          />
                        ))}
                      </FormLayout.Group>
                    ))}
                  </FormLayout>
                </Card.Section>
              </Card>
            )}
          </FormLayout>
        </Modal.Section>
      </Modal>
      
      {/* Size Conversion Modal */}
      <Modal
        open={isConversionModalOpen}
        onClose={closeConversionModal}
        title="Convert Sizechart to Another Country"
        primaryAction={{
          content: isConverting ? 'Converting...' : 'Convert and Save',
          onAction: handleConvertSizechart,
          disabled: isConverting || !conversionTargetCountry
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: closeConversionModal,
            disabled: isConverting
          }
        ]}
      >
        <Modal.Section>
          <FormLayout>
            {conversionSourceSizechart && (
              <Banner status="info">
                <p><strong>Source Sizechart:</strong> {conversionSourceSizechart.name}</p>
                <p><strong>Source Country:</strong> {conversionSourceSizechart.countries?.[0] || 'US'}</p>
              </Banner>
            )}
            
            {isConverting ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spinner size="large" />
                <div style={{ marginTop: '10px' }}>
                  <p>Converting sizes using AI...</p>
                  <p>This may take a few moments</p>
                </div>
              </div>
            ) : (
              <>
                <Select
                  label="Target Country"
                  options={countryOptions.filter(country => 
                    !conversionSourceSizechart?.countries?.includes(country.value)
                  )}
                  value={conversionTargetCountry}
                  onChange={setConversionTargetCountry}
                  error={conversionError && !conversionTargetCountry ? 'Target country is required' : undefined}
                  helpText="Select the country standard to convert this sizechart to"
                />
                
                <Select
                  label="Clothing Category"
                  options={[
                    { label: 'Women\'s Clothing', value: 'women' },
                    { label: 'Men\'s Clothing', value: 'men' },
                    { label: 'Kids\'s Clothing', value: 'kids' }
                  ]}
                  value={conversionCategory}
                  onChange={setConversionCategory}
                  helpText="Select the clothing category for accurate size conversion"
                />
                
                {conversionError && (
                  <Banner status="critical">
                    <p>{conversionError}</p>
                  </Banner>
                )}
                
                <Banner status="info">
                  <p><strong>How this works:</strong></p>
                  <p>Our AI will analyze your sizechart and convert each size to the equivalent in the target country's sizing system.</p>
                  <p>The conversion takes into account the specific measurements and clothing category.</p>
                  <p>This will use 1 AI credit from your account.</p>
                </Banner>
              </>
            )}
          </FormLayout>
        </Modal.Section>
      </Modal>
      {/* Chart Preview Modal */}
      <Modal
        open={isChartPreviewModalOpen}
        onClose={() => setIsChartPreviewModalOpen(false)}
        title={previewSizechart ? `${previewSizechart.name} - Sizechart Preview` : 'Sizechart Preview'}
        large
      >
        <Modal.Section>
          {previewSizechart && (
            <>
              <LegacyStack vertical spacing="loose">
                <LegacyStack distribution="equalSpacing">
                  <div>
                    <Text variant="headingMd">{previewSizechart.name}</Text>
                    {previewSizechart.description && (
                      <Text variant="bodyMd" color="subdued">{previewSizechart.description}</Text>
                    )}
                  </div>
                  <LegacyStack spacing="tight">
                    <Badge>{previewSizechart.category || 'women'}</Badge>
                    {(previewSizechart.countries || ['US']).map(country => (
                      <Badge key={country}>{country}</Badge>
                    ))}
                  </LegacyStack>
                </LegacyStack>
                
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {previewSizechart.columns.map((col, index) => (
                          <th key={index} style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'left' }}>
                            {col.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewSizechart.rows.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          <td style={{ padding: '10px', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>
                            {row.size}
                          </td>
                          {row.values.map((value, valueIndex) => (
                            <td key={valueIndex} style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>
                              {value || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </LegacyStack>
            </>
          )}
        </Modal.Section>
      </Modal>
      
      {toastMarkup}
    </Layout>
  );
}
